import React, { FC } from 'react';

import {
  Box,
  Button,
  Chip,
  Divider,
  PlanFeatures,
  Stack,
  Typography,
} from 'components';

import { useTranslation } from 'third-party';

import { SUPPORT_EMAIL } from 'constants/common';

import { BillingPlan } from 'modules/billing';

import { formatUsd } from 'utils/helper';

type Props = {
  plans: BillingPlan[];
  currentPlanCode: string | null;
  isStripeConfigured: boolean;
  isPending: boolean;
  isUnlimited: boolean;
  onSelect: (planCode: string) => void;
};

export const PlanPicker: FC<Props> = ({
  plans,
  currentPlanCode,
  isStripeConfigured,
  isPending,
  isUnlimited,
  onSelect,
}) => {
  const { t } = useTranslation();

  const extraLabel = (plan: BillingPlan) => {
    if (plan.isCustom) return t('billing.plan.extraNegotiated');
    if (plan.extraCheckPrice === null)
      return t('billing.plan.extraPackPricing');
    return t('billing.plan.extraEach', {
      price: formatUsd(plan.extraCheckPrice),
    });
  };

  const priceLabel = (plan: BillingPlan) => {
    if (plan.isCustom) return t('billing.plan.priceCustom');
    if (plan.monthlyPrice <= 0) return t('billing.plan.priceFree');
    return t('billing.plan.priceMonthly', {
      price: formatUsd(plan.monthlyPrice),
    });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
      }}
    >
      {plans.map(plan => {
        const isCurrent = plan.code === currentPlanCode;
        const needsSales = plan.isCustom;

        const canCheckout = !needsSales && !isCurrent && !isUnlimited;
        const isPaid = plan.monthlyPrice > 0;
        const isPayable = plan.isSelfServable && isStripeConfigured;

        return (
          <Box
            key={plan.id}
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: isCurrent ? 'primary.main' : 'divider',
              borderRadius: 1,
              backgroundColor: isCurrent ? 'action.hover' : 'transparent',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              mb={0.5}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
                {plan.name}
              </Typography>
              {isCurrent && (
                <Chip
                  size="small"
                  color="primary"
                  label={t('billing.plan.current')}
                />
              )}
            </Stack>

            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {priceLabel(plan)}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={0.5} flexGrow={1}>
              <PlanFeatures plan={plan} />
              <Typography variant="body2" color="text.secondary">
                {extraLabel(plan)}
              </Typography>
            </Stack>

            <Box mt={2}>
              {needsSales ? (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                    t('billing.plan.salesSubject', { plan: plan.name }),
                  )}`}
                >
                  {t('billing.plan.contactSales')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant={isCurrent ? 'outlined' : 'contained'}
                  size="small"
                  disabled={!canCheckout || isPending}
                  onClick={() => onSelect(plan.code)}
                  sx={{
                    color: isCurrent ? undefined : 'primary.contrastText',
                    '&.Mui-disabled': {
                      color: isCurrent ? undefined : 'primary.contrastText',
                    },
                  }}
                >
                  {isCurrent
                    ? t('billing.plan.currentPlanButton')
                    : t('billing.plan.choose', { plan: plan.name })}
                </Button>
              )}

              {canCheckout && isPaid && !isPayable && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {t('billing.plan.notConfigured')}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
